import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  FiEdit, FiTrash2, FiX, FiPlus, FiTag,
  FiSearch, FiDownload, FiChevronLeft, FiChevronRight,
} from "react-icons/fi";

const API_BASE = "https://estatebackend.pixelmindsolutions.com";
const PER_PAGE = 6;

/* ─── Portal Modal ─── */
const Modal = ({ title, children, onClose, darkMode }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return createPortal(
    <div
      style={{ position: "fixed", inset: 0, zIndex: 9999 }}
      className="flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-md rounded-2xl shadow-2xl p-6 ${darkMode ? "bg-gray-800 text-white" : "bg-white"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-bold">{title}</h3>
          <button
            onClick={onClose}
            className={`w-8 h-8 flex items-center justify-center rounded-full transition ${darkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
          >
            <FiX size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
};

/* ─── Main Component ─── */
const Categories = ({ darkMode }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  /* Create */
  const [openCreate, setOpenCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newImage, setNewImage] = useState(null);
  const [creating, setCreating] = useState(false);

  /* Edit */
  const [openEdit, setOpenEdit] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editName, setEditName] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [updating, setUpdating] = useState(false);

  /* Delete */
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* ── Fetch ── */
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/auth/getall-categories`);
      setCategories(res.data.categories || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []);

  /* ── Search filter ── */
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return categories;
    return categories.filter((c) => c.name.toLowerCase().includes(q));
  }, [categories, search]);

  /* ── Pagination ── */
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return filtered.slice(start, start + PER_PAGE);
  }, [filtered, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [search]);

  /* ── CSV Export ── */
  const exportCSV = () => {
    const rows = [
      ["#", "Name", "Status", "Created At"],
      ...filtered.map((c, i) => [
        i + 1,
        `"${c.name}"`,
        c.isActive ? "Active" : "Inactive",
        new Date(c.createdAt).toLocaleDateString("en-IN"),
      ]),
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "categories.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── Create ── */
  const createCategory = async () => {
    if (!newName.trim()) return;
    try {
      setCreating(true);
      const fd = new FormData();
      fd.append("name", newName.trim());
      if (newImage) fd.append("image", newImage);
      await axios.post(`${API_BASE}/api/auth/category/create`, fd);
      setOpenCreate(false);
      setNewName("");
      setNewImage(null);
      fetchCategories();
    } finally {
      setCreating(false);
    }
  };

  /* ── Update ── */
  const updateCategory = async () => {
    if (!editName.trim() || !editItem) return;
    try {
      setUpdating(true);
      const fd = new FormData();
      fd.append("name", editName.trim());
      if (editImage) fd.append("image", editImage);
      await axios.put(`${API_BASE}/api/auth/category/${editItem._id}`, fd);
      setOpenEdit(false);
      setEditItem(null);
      setEditName("");
      setEditImage(null);
      fetchCategories();
    } finally {
      setUpdating(false);
    }
  };

  /* ── Delete ── */
  const confirmDelete = async () => {
    if (!deleteItem) return;
    try {
      setDeleting(true);
      await axios.delete(`${API_BASE}/api/auth/category/${deleteItem._id}`);
      setOpenDelete(false);
      setDeleteItem(null);
      fetchCategories();
    } finally {
      setDeleting(false);
    }
  };

  /* ── Pagination page numbers ── */
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
    .reduce((acc, p, i, arr) => {
      if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
      acc.push(p);
      return acc;
    }, []);

  return (
    <div className={`min-h-screen p-6 ${darkMode ? "bg-gray-900" : "bg-gray-100"}`}>
      <div className={`max-w-4xl mx-auto rounded-xl shadow p-6 ${darkMode ? "bg-gray-800 text-white" : "bg-white"}`}>

        {/* ── Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div>
            <h2 className="text-xl font-bold text-blue-600">Categories</h2>
            {!loading && (
              <p className={`text-xs mt-0.5 ${darkMode ? "text-gray-400" : "text-gray-400"}`}>
                {filtered.length} of {categories.length} categories
              </p>
            )}
          </div>
          <button
            onClick={() => setOpenCreate(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition self-start sm:self-auto"
          >
            <FiPlus size={15} /> Add Category
          </button>
        </div>

        {/* ── Search + Export toolbar ── */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
            <input
              type="text"
              placeholder="Search categories…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full pl-9 pr-9 py-2.5 rounded-xl border text-sm outline-none focus:ring-2 focus:ring-blue-400 transition ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "border-gray-200 bg-gray-50"}`}
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <FiX size={14} />
              </button>
            )}
          </div>
          <button
            onClick={exportCSV}
            disabled={filtered.length === 0}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap ${darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            <FiDownload size={15} className="text-green-500" />
            Export CSV
          </button>
        </div>

        {/* ── Table ── */}
        <div className={`overflow-x-auto rounded-xl border ${darkMode ? "border-gray-700" : "border-gray-100"}`}>
          <table className="min-w-full text-sm">
            <thead className={darkMode ? "bg-gray-700" : "bg-blue-50"}>
              <tr>
                {["#", "Image", "Category Name", "Status", "Created", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-semibold text-sm whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-16">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm">Loading categories…</span>
                    </div>
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-16">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <FiTag size={28} />
                      <span className="text-sm">{search ? "No matching categories" : "No categories found"}</span>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((c, i) => (
                  <tr
                    key={c._id}
                    className={`border-t transition-colors ${darkMode ? "border-gray-700 hover:bg-gray-700/40" : "border-gray-100 hover:bg-blue-50/40"}`}
                  >
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-gray-400">
                        {(currentPage - 1) * PER_PAGE + i + 1}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {c.image ? (
                        <img src={c.image} alt={c.name}
                          className="w-10 h-10 rounded-xl object-cover border border-gray-200"
                          onError={(e) => { e.target.src = "https://via.placeholder.com/40"; }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                          <FiTag size={16} className="text-blue-500" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold">{c.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${c.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {c.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                        {new Date(c.createdAt).toLocaleDateString("en-IN")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          title="Edit"
                          onClick={() => { setEditItem(c); setEditName(c.name); setOpenEdit(true); }}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-100 transition"
                        >
                          <FiEdit size={14} />
                        </button>
                        <button
                          title="Delete"
                          onClick={() => { setDeleteItem(c); setOpenDelete(true); }}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 transition"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between mt-5 flex-wrap gap-3">
            <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Page {currentPage} of {totalPages} &middot; {filtered.length} results
            </p>
            <div className="flex items-center gap-1.5">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg border disabled:opacity-40 transition ${darkMode ? "border-gray-600 hover:bg-gray-700" : "border-gray-200 hover:bg-gray-50"}`}
              >
                <FiChevronLeft size={15} />
              </button>

              {pageNumbers.map((p, i) =>
                p === "..." ? (
                  <span key={`dots-${i}`} className={`text-xs px-1 ${darkMode ? "text-gray-500" : "text-gray-400"}`}>…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition ${
                      currentPage === p
                        ? "bg-blue-600 text-white"
                        : darkMode
                        ? "border border-gray-600 hover:bg-gray-700 text-gray-300"
                        : "border border-gray-200 hover:bg-gray-50 text-gray-600"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg border disabled:opacity-40 transition ${darkMode ? "border-gray-600 hover:bg-gray-700" : "border-gray-200 hover:bg-gray-50"}`}
              >
                <FiChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Create Modal ── */}
      {openCreate && (
        <Modal title="Add Category" onClose={() => { setOpenCreate(false); setNewName(""); setNewImage(null); }} darkMode={darkMode}>
          <div className="space-y-4">
            <div>
              <label className={`text-xs font-medium mb-1 block ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Category Name *</label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Apartments"
                className={`w-full rounded-xl px-4 py-2.5 border text-sm outline-none focus:ring-2 focus:ring-blue-400 transition ${darkMode ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" : "border-gray-200"}`}
              />
            </div>
            <div>
              <label className={`text-xs font-medium mb-1 block ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Image (optional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setNewImage(e.target.files[0])}
                className={`w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
              />
              {newImage && <p className="text-xs text-green-500 mt-1">✓ {newImage.name}</p>}
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => { setOpenCreate(false); setNewName(""); setNewImage(null); }}
              className={`flex-1 px-4 py-2.5 rounded-xl border text-sm font-medium transition ${darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
              Cancel
            </button>
            <button onClick={createCategory} disabled={creating || !newName.trim()}
              className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition disabled:opacity-60 flex items-center justify-center gap-2">
              {creating ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiPlus size={14} />}
              {creating ? "Creating…" : "Create"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Edit Modal ── */}
      {openEdit && editItem && (
        <Modal title="Edit Category" onClose={() => { setOpenEdit(false); setEditItem(null); setEditName(""); setEditImage(null); }} darkMode={darkMode}>
          <div className="space-y-4">
            {editItem.image && !editImage && (
              <div className="flex items-center gap-3">
                <img src={editItem.image} alt={editItem.name} className="w-12 h-12 rounded-xl object-cover border border-gray-200"
                  onError={(e) => { e.target.src = "https://via.placeholder.com/48"; }} />
                <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Current image</p>
              </div>
            )}
            {editImage && <p className="text-xs text-green-500">✓ New: {editImage.name}</p>}
            <div>
              <label className={`text-xs font-medium mb-1 block ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Category Name *</label>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className={`w-full rounded-xl px-4 py-2.5 border text-sm outline-none focus:ring-2 focus:ring-blue-400 transition ${darkMode ? "bg-gray-700 border-gray-600 text-white" : "border-gray-200"}`}
              />
            </div>
            <div>
              <label className={`text-xs font-medium mb-1 block ${darkMode ? "text-gray-400" : "text-gray-500"}`}>Replace Image (optional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setEditImage(e.target.files[0])}
                className={`w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-amber-50 file:text-amber-600 hover:file:bg-amber-100 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
              />
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => { setOpenEdit(false); setEditItem(null); setEditName(""); setEditImage(null); }}
              className={`flex-1 px-4 py-2.5 rounded-xl border text-sm font-medium transition ${darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
              Cancel
            </button>
            <button onClick={updateCategory} disabled={updating || !editName.trim()}
              className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition disabled:opacity-60 flex items-center justify-center gap-2">
              {updating ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiEdit size={14} />}
              {updating ? "Updating…" : "Update"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirm Modal ── */}
      {openDelete && deleteItem && (
        <Modal title="Delete Category" onClose={() => { setOpenDelete(false); setDeleteItem(null); }} darkMode={darkMode}>
          <div className="flex flex-col items-center text-center gap-3 py-2">
            {deleteItem.image && (
              <img src={deleteItem.image} alt={deleteItem.name} className="w-14 h-14 rounded-xl object-cover border border-gray-200" />
            )}
            <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
              Are you sure you want to delete <span className="font-semibold">{deleteItem.name}</span>? This cannot be undone.
            </p>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => { setOpenDelete(false); setDeleteItem(null); }}
              className={`flex-1 px-4 py-2.5 rounded-xl border text-sm font-medium transition ${darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
              Cancel
            </button>
            <button onClick={confirmDelete} disabled={deleting}
              className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition disabled:opacity-60 flex items-center justify-center gap-2">
              {deleting ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiTrash2 size={14} />}
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Categories;